import React, { useEffect, useState } from "react";
import SecurityIcon from "@mui/icons-material/Security";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import VpnKeyIcon from "@mui/icons-material/VpnKey";
import SearchIcon from "@mui/icons-material/Search";
import { NGROK } from "../../../../APIs";
import axios from "axios";
import { myLocalStorage } from "../../../../components/StorageHelper";
import TailwindSwitch from "../../../../tailwind-ui/components/TailwindSwitch";

const BLUE = "#3b83f6";
const RESOURCE_ROLE_FILTERS = new Set([
  "virtual_machine",
  "storage",
  "database",
  "kubernetes",
]);
const EMPTY_RESOURCE_ROLES = {
  virtual_machine: [],
  storage: [],
  database: [],
  kubernetes: [],
};

const sortResourceRoles = (roles, isPreferred) =>
  [...roles].sort((first, second) => {
    const firstName = first?.roleName || "";
    const secondName = second?.roleName || "";
    const priorityDifference =
      Number(!isPreferred(firstName)) - Number(!isPreferred(secondName));
    return (
      priorityDifference ||
      firstName.localeCompare(secondName, undefined, { sensitivity: "base" })
    );
  });

const filterRoles = (roles, filter) => {
  if (!roles) return [];
  if (filter === "default") {
    return roles.filter((role) => role.roleType === "BuiltInRole");
  }
  if (filter === "custom") {
    return roles.filter((role) => role.roleType !== "BuiltInRole");
  }
  return roles;
};

const getFilteredRoles = (roles, resourceRoles, filter, search) => {
  const source = RESOURCE_ROLE_FILTERS.has(filter)
    ? resourceRoles[filter] || []
    : roles || [];
  return filterRoles(
    source.filter(
      (role) =>
        role?.roleName &&
        role.roleName.toLowerCase().includes(search.toLowerCase())
    ),
    filter
  );
};

const Sk = ({ className = "" }) => (
  <div className={`animate-pulse rounded-md bg-gray-200 dark:bg-gray-700/60 ${className}`} />
);

const RolesListSkeleton = () => (
  <div className="flex min-h-0 flex-1 flex-col w-full gap-4">
    <div className="role-dashboard-grid">
      <div className="role-panel">
        <div className="role-panel-header">
          <div>
            <Sk className="h-4 w-24 rounded" />
            <Sk className="h-3 w-52 rounded mt-2" />
          </div>
          <Sk className="h-6 w-9 rounded-full" />
        </div>

        <div className="role-search-shell">
          <Sk className="h-10 w-full rounded-xl" />
        </div>

        <div className="role-list">
          {Array.from({ length: 10 }).map((_, idx) => (
            <div
              key={idx}
              className="role-row"
            >
              <div className="flex items-center gap-3 min-w-0 flex-1">
                <Sk className="h-10 w-10 rounded-xl flex-shrink-0" />
                <div className="min-w-0 flex-1">
                  <Sk className="h-4 w-2/3 rounded" />
                  <Sk className="h-3 w-1/3 rounded mt-2" />
                </div>
              </div>
              <Sk className="h-6 w-12 rounded-full flex-shrink-0" />
            </div>
          ))}
        </div>
      </div>

      <div className="role-panel">
        <div className="role-detail-header">
          <Sk className="h-10 w-10 rounded-xl flex-shrink-0" />
          <div className="min-w-0 flex-1">
            <Sk className="h-5 w-2/3 rounded" />
            <Sk className="h-4 w-1/3 rounded mt-2" />
          </div>
        </div>

        <div className="role-detail-tabs">
          <Sk className="h-8 w-20 rounded-lg" />
          <Sk className="h-8 w-28 rounded-lg" />
        </div>

        <div className="role-detail-content role-info-stack">
          {Array.from({ length: 4 }).map((_, idx) => (
            <div key={idx} className="role-info-card">
              <Sk className="h-3 w-24 rounded" />
              <Sk className="h-4 w-full rounded mt-2" />
            </div>
          ))}
        </div>
      </div>
    </div>
  </div>
);

const AzureGenericRoles = ({ subscription, roleFilter = "all" }) => {
  const [search, setSearch] = useState("");
  const [selectedRole, setSelectedRole] = useState(null);
  const [selectedRoleActions, setSelectedRoleActions] = useState(null);
  const [selectedDetailsTab, setSelectedDetailsTab] = useState(0);
  const [actionsTab, setActionsTab] = useState("actions");
  const latestTenant = myLocalStorage.getItem("latestTenant")?.tenantName;
  const [roles, setRoles] = useState([]);
  const [resourceRoles, setResourceRoles] = useState(EMPTY_RESOURCE_ROLES);
  const [filter, setFilter] = useState(roleFilter);
  const [loading, setLoading] = useState(true);
  const [isDetailsLoading, setIsDetailsLoading] = useState(false);

  const getTheSelectedRoleDefinition = async (role) => {
    setIsDetailsLoading(true);
    try {
      const response = await axios.get(
        `${NGROK}/api/azureResources/v1/getAzureRoleDefinitionById?id=${role.id}&tenantName=${latestTenant}`
      );
      if (response.status === 200 && response?.data) {
        setSelectedRoleActions(response.data);
      } else {
        setSelectedRoleActions(null);
      }
    } catch (error) {
      setSelectedRoleActions(null);
    }
    setIsDetailsLoading(false);
  };

  const getAllRoles = async (subscription) => {
    const response = await axios.get(
      `${NGROK}/api/azureResources/v1/getRoleDefinitionsName?tenantName=${latestTenant}&subsId=${subscription?.azureSubscriptionId}`
    );
    if (response.status === 200) return response.data;
    else return [];
  };

  const getAzureResourceRoles = async (subscription) => {
    const response = await axios.get(
      `${NGROK}/api/azureResources/v1/getAzureResourcesRoles`,
      {
        params: {
          tenantName: latestTenant,
          subsId: subscription?.azureSubscriptionId,
        },
      }
    );
    return {
      virtual_machine: sortResourceRoles(
        response.data?.virtual_machine || [],
        (name) => name.toLowerCase().includes("virtual machine")
      ),
      storage: sortResourceRoles(
        response.data?.storage || [],
        (name) => name.toLowerCase().includes("storage")
      ),
      database: sortResourceRoles(
        response.data?.database || [],
        (name) => name.toLowerCase().includes("sql")
      ),
      kubernetes: sortResourceRoles(
        response.data?.kubernetes || [],
        (name) => name.toLowerCase().includes("kubernetes")
      ),
    };
  };

  const fetchData = async (initialLoad = false) => {
    try {
      if (!subscription?.azureSubscriptionId) {
        setRoles([]);
        setResourceRoles(EMPTY_RESOURCE_ROLES);
        setSelectedRole(null);
        setSelectedRoleActions(null);
        setLoading(false);
        return;
      };
      const [rolesData, resourceRolesData] = await Promise.all([
        getAllRoles(subscription),
        getAzureResourceRoles(subscription).catch(() => EMPTY_RESOURCE_ROLES),
      ]);
      setRoles(rolesData);
      setResourceRoles(resourceRolesData);
      setLoading(false);
      if (initialLoad) {
        const filteredRoles = getFilteredRoles(
          rolesData,
          resourceRolesData,
          filter,
          search
        );
        let selectRole = filteredRoles.length > 0 ? filteredRoles[0] : null;
        setSelectedRole(selectRole);
        if (selectRole) getTheSelectedRoleDefinition(selectRole);
      }
    } catch (error) {
      console.error("Error fetching data", error);
      setRoles([]);
      setResourceRoles(EMPTY_RESOURCE_ROLES);
      setSelectedRole(null);
      setSelectedRoleActions(null);
    }
    setLoading(false);
  };

  const handleFilterChange = (newFilter) => {
    setFilter(newFilter);
    const filteredRoles = getFilteredRoles(
      roles,
      resourceRoles,
      newFilter,
      search
    );
    const selectRole = filteredRoles.length > 0 ? filteredRoles[0] : null;
    setSelectedRole(selectRole);
    setSelectedDetailsTab(0);
    if (selectRole) getTheSelectedRoleDefinition(selectRole);
  };

  const handleRoleSearchChange = (event) => {
    setSearch(event.target.value);
  };

  const handleSearchSubmit = () => {
    // Search is already reactive via state
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter") {
      handleSearchSubmit();
    }
  };

  const handleRoleClick = (role) => {
    setSelectedRole(role);
    setSelectedDetailsTab(0);
    setActionsTab("actions");
    getTheSelectedRoleDefinition(role);
  };

  const filteredRoles = getFilteredRoles(
    roles,
    resourceRoles,
    filter,
    search
  );

  const updatePublishedRole = (azureId, published) => {
    const updateRoles = (roleList) =>
      roleList.map((role) =>
        role.azureId === azureId ? { ...role, published } : role
      );
    setRoles(updateRoles);
    setResourceRoles((previous) => ({
      virtual_machine: updateRoles(previous.virtual_machine),
      storage: updateRoles(previous.storage),
      database: updateRoles(previous.database),
      kubernetes: updateRoles(previous.kubernetes),
    }));
  };

  const handlePublishToggle = async (event, azureId, role) => {
    const published = event.target.checked;
    updatePublishedRole(azureId, published);

    let requestData = {
      resourceId: azureId,
      wsTenantName: latestTenant,
      flag: event.target.checked,
      type: "ROLE_DEFINITION",
      resourceAccountId: role?.subscriptionId,
    };

    try {
      await axios.patch(`${NGROK}/api/azureResources/v1/publish`, requestData);
    } catch (error) {
      updatePublishedRole(azureId, !published);
    }
  };

  useEffect(() => {
    setLoading(Boolean(subscription?.azureSubscriptionId));
    fetchData(true);
  }, [latestTenant, subscription]);

  useEffect(() => {
    handleFilterChange(roleFilter);
  }, [roleFilter]);

  const selectedKey = selectedRole?.id;
  const isBuiltIn = selectedRole?.roleType === "BuiltInRole";

  const renderActionsContent = () => {
    if (isDetailsLoading) {
      return (
        <div className="flex justify-center py-8">
          <div
            className="w-6 h-6 border-2 border-t-transparent rounded-full animate-spin"
            style={{ borderColor: BLUE, borderTopColor: "transparent" }}
          />
        </div>
      );
    }

    if (!selectedRoleActions) {
      return (
        <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
          <VpnKeyIcon sx={{ fontSize: 36, color: "rgba(0,0,0,0.25)" }} />
          <p className="text-sm mt-2">No actions available</p>
        </div>
      );
    }

    const actionsData = actionsTab === "actions"
      ? selectedRoleActions?.actions || []
      : selectedRoleActions?.notActions || [];

    return (
      <div>
        {/* Actions/NotActions Toggle */}
        <div className="flex gap-4 mb-4 border-b border-gray-200 dark:border-gray-700">
          {[
            { key: "actions", label: "Actions" },
            { key: "notActions", label: "Not Actions" },
          ].map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActionsTab(tab.key)}
              className={`pb-2 text-xs font-medium border-b-2 transition-all ${actionsTab === tab.key
                ? "border-b-2"
                : "text-muted-foreground border-transparent hover:text-foreground/80"
                }`}
              style={actionsTab === tab.key ? { color: BLUE, borderColor: BLUE } : undefined}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <div className="flex items-center gap-2 mb-4">
          <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
            {actionsTab === "actions" ? "Actions" : "Not Actions"}
          </span>
          <span
            className="px-2 py-0.5 text-xs font-semibold rounded-full"
            style={{ backgroundColor: "rgba(59,131,246,0.12)", color: BLUE }}
          >
            {actionsData?.length || 0}
          </span>
        </div>

        <div className="space-y-2">
          {actionsData?.length > 0 ? (
            actionsData.map((action, idx) => (
              <div key={idx} className="flex items-center gap-2 px-3 py-2 bg-gray-50 dark:bg-gray-800/50 rounded-xl">
                <div className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: BLUE }} />
                <span className="text-sm text-foreground/85 font-mono break-all">{action}</span>
              </div>
            ))
          ) : (
            <div className="flex flex-col items-center justify-center py-8 text-muted-foreground">
              <VpnKeyIcon sx={{ fontSize: 32, color: "rgba(0,0,0,0.25)" }} />
              <p className="text-sm mt-2">No {actionsTab === "actions" ? "actions" : "not actions"} defined</p>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <>
      {loading ? (
        <RolesListSkeleton />
      ) : (
        <div className="flex min-h-0 flex-1 flex-col w-full gap-4">
          <div className="role-dashboard-grid">
            {/* Left Column - Roles List */}
            <div className="role-panel">
              <div className="role-panel-header">
                <div>
                  <div className="role-panel-title">Roles</div>
                  <div className="role-panel-subtitle">Select a role to inspect permissions</div>
                </div>
                <span className="role-count-pill">{filteredRoles?.length || 0}</span>
              </div>

              <div className="role-search-shell">
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Search roles..."
                    value={search}
                    onChange={handleRoleSearchChange}
                    onKeyDown={handleKeyDown}
                    className="role-search-input"
                  />
                  <button
                    onClick={handleSearchSubmit}
                    className="role-search-icon"
                    aria-label="Search"
                    type="button"
                  >
                    <SearchIcon fontSize="small" />
                  </button>
                </div>
              </div>

              <div className="role-list">
                {filteredRoles?.length > 0 ? (
                  filteredRoles.map((role, index) => {
                    const roleKey = role.id || index;
                    const isSelected = roleKey === selectedKey;

                    return (
                      <div
                        key={roleKey}
                        onClick={() => handleRoleClick(role)}
                        className={`role-row ${isSelected ? "active" : ""}`}
                      >
                        <div className="flex items-center gap-3 min-w-0">
                          <div className="role-avatar">
                            <SecurityIcon sx={{ color: BLUE, fontSize: 20 }} />
                          </div>
                          <div className="role-row-text">
                            <p className="role-row-title">
                              {role.roleName}
                            </p>
                            <p className="role-row-subtitle">
                              {role.roleType === "BuiltInRole" ? "Built-in Role" : "Custom Role"}
                            </p>
                          </div>
                        </div>

                        <div className="flex items-center flex-shrink-0" onClick={(e) => e.stopPropagation()}>
                          <TailwindSwitch
                            isDisabled={false}
                            action={(e, data) => handlePublishToggle(e, data.azureId, data)}
                            data={role}
                            isChecked={role?.published}
                          />
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <div className="role-empty-state">
                    <SecurityIcon sx={{ fontSize: 44, color: "rgba(0,0,0,0.25)" }} />
                    <p className="text-sm mt-2">No roles found</p>
                  </div>
                )}
              </div>
            </div>

            {/* Right Column - Details Panel */}
            <div className="role-panel">
              {selectedRole ? (
                <>
                  {/* Role Header */}
                  <div className="role-detail-header">
                    <div className="role-avatar">
                      <SecurityIcon sx={{ color: BLUE, fontSize: 22 }} />
                    </div>
                    <div className="min-w-0">
                      <h3 className="role-detail-title">{selectedRole?.roleName}</h3>
                      <p className="role-detail-subtitle">
                        {isBuiltIn ? "Built-in Role" : "Custom Role"}
                      </p>
                    </div>
                  </div>

                  {/* Tabs */}
                  <div className="role-detail-tabs">
                    <button
                      onClick={() => setSelectedDetailsTab(0)}
                      className={`role-detail-tab ${selectedDetailsTab === 0 ? "active" : ""}`}
                    >
                      <InfoOutlinedIcon fontSize="small" />
                      Info
                    </button>
                    <button
                      onClick={() => setSelectedDetailsTab(1)}
                      className={`role-detail-tab ${selectedDetailsTab === 1 ? "active" : ""}`}
                    >
                      <VpnKeyIcon fontSize="small" />
                      Permissions
                    </button>
                  </div>

                  {/* Tab Content */}
                  <div className="role-detail-content">
                    {selectedDetailsTab === 0 && (
                      <div className="role-info-stack">
                        <div className="role-info-card">
                          <div className="role-info-label">Role Name</div>
                          <div className="role-info-value">
                            {selectedRole?.roleName || "—"}
                          </div>
                        </div>

                        <div className="role-info-card">
                          <div className="role-info-label">Role ID</div>
                          <div className="role-info-value font-mono">
                            {selectedRole?.id || "—"}
                          </div>
                        </div>

                        <div className="role-info-card">
                          <div className="role-info-label">Azure ID</div>
                          <div className="role-info-value font-mono">
                            {selectedRole?.azureId || "—"}
                          </div>
                        </div>

                        <div className="role-info-card">
                          <div className="role-info-label">Role Type</div>
                          <div className="role-info-value">
                            {selectedRole?.roleType || "—"}
                          </div>
                        </div>

                        <div className="role-info-card">
                          <div className="role-info-label">Description</div>
                          <div className="role-info-value">
                            {selectedRoleActions?.description || selectedRole?.description || "—"}
                          </div>
                        </div>
                      </div>
                    )}

                    {selectedDetailsTab === 1 && renderActionsContent()}
                  </div>
                </>
              ) : (
                <div className="role-empty-state">
                  <SecurityIcon sx={{ fontSize: 52, color: "rgba(0,0,0,0.25)" }} />
                  <p className="text-sm mt-2">Select a role to view details</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AzureGenericRoles;
